/* eslint-disable react/prop-types */
import React, { memo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components/macro';
import {
  Text,
  LoadingDot,
  Table,
} from 'components/atoms';
import { PopoverActions } from 'components/molecules'
import { useRouterContext } from 'hooks';
import { injectIntl, FormattedMessage } from 'react-intl'
import { intlType } from 'types'
import { useUserContext, isUserSubwebAdmin } from 'context/UserContext';

const SubwebsTable = memo(({
  subwebs,
  loading,
  intl,
  noDataText,
  page
}) => {
  const { history } = useRouterContext();
  const [{ user }] = useUserContext()
  return (
    <Table
      data={subwebs}
      columns={[
        {
          Header: intl.formatMessage({ id: 'subwebs.title' }),
          accessor: 'description',
          Cell: ({ value, original: { slug: subweb } }) => (
            <Table.CellLink to={`/${encodeURIComponent(subweb)}`}>
              <Cell value={value} loadingDots={loading} />
            </Table.CellLink>
          ),
        },
        {
          Header: intl.formatMessage({ id: 'subwebs.slug' }),
          accessor: 'slug',
          Cell: ({ value, original: { slug: subweb } }) => (
            <Table.CellLink to={`/${encodeURIComponent(subweb)}`}>
              <Cell value={`/${value}`} loading={loading} />
            </Table.CellLink>
          )
        },
        {
          Header: '',
          accessor: 'actions',
          Cell: ({ original: { slug: subweb } }) => {
            const isAdmin = (user && user.admin) || isUserSubwebAdmin(user, subweb)
            return (
              <Table.CellLink to={`/${encodeURIComponent(subweb)}`}>
                <CellEnd>
                  <PopoverActions
                    disabled={loading}
                    actions={[
                      {
                        text: <FormattedMessage id="subweb.edit" />,
                        onClick: () => {
                          history.push(`/${encodeURIComponent(subweb)}`)
                        },
                      },
                      {
                        text: <FormattedMessage id="subweb.edit.menu" />,
                        onClick: () => {
                          history.push(`/${encodeURIComponent(subweb)}/editor/pt/nav`)
                        },
                      },
                      {
                        text: <FormattedMessage id="resources.view" />,
                        onClick: () => {
                          history.push(`/${encodeURIComponent(subweb)}/assets`)
                        },
                      },
                      ...(isAdmin
                        ? [
                          {
                            text: <FormattedMessage id="subweb.people" />,
                            onClick: () => {
                              history.push(`/${encodeURIComponent(subweb)}/people`)
                            },
                          },
                          {
                            text: <FormattedMessage id="subweb.permissions" />,
                            onClick: () => {
                              history.push(`/${encodeURIComponent(subweb)}/permissions`)
                            },
                          },
                          {
                            text: <FormattedMessage id="subweb.settings" />,
                            onClick: () => {
                              history.push(`/${encodeURIComponent(subweb)}/settings`)
                            },
                          },
                        ]
                        : []
                      ),
                    ]}
                    position="bottom"
                    align="start"
                    handlerWidth={72}
                  />
                </CellEnd>
              </Table.CellLink>
            )
          },
        },
      ]}
      defaultPageSize={10}
      minRows={0}
      className="-striped -highlight"
      noDataText={noDataText || 'Não foram encontradas subwebs'}
      sortable={false}
      showPagination={false}
      loading={loading}
      loadingText="A carregar..."
      page={page}
      clearRowPadding
    />
  );
});

const Cell = ({ value, loadingDots, loading }) => {
  if (loadingDots) return <LoadingDot />
  if (loading) return <LoadingCell />
  if (!value) {
    return (
      <Text modifiers={['lighter']}>
        <FormattedMessage id="error.missing" />
      </Text>
    )
  }
  return <Text modifiers={['light']}>{value}</Text>
};

const LoadingCell = styled.div`
  width: 80px;
  height: ${props => props.theme.sizes.s};
  background-color: ${props => props.theme.colors.bgLighterS};
`

const CellEnd = styled.div`
  justify-content: flex-end;
  width: 100%;
  display: flex;
`

SubwebsTable.propTypes = {
  subwebs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      slug: PropTypes.string.isRequired,
      description: PropTypes.string,
      color: PropTypes.string,
    })
  ),
  loading: PropTypes.bool,
  intl: intlType.isRequired,
  noDataText: PropTypes.string,
  page: PropTypes.number,
};

SubwebsTable.defaultProps = {
  subwebs: [],
  loading: false,
  noDataText: null,
  page: 0
}


export default injectIntl(SubwebsTable);
